// The search of the open screen (R-U23, um/2026-10-31/02): a screen gives the global search (the rail's icon, ⌘K) what it holds — the
// letters of the open mailbox, the articles of a section, the pages of a site, the forms, the rows of a section — and the palette shows
// them first, under «На этом экране», then the whole system. Screens have no search fields of their own in the list column.

import { useEffect, useRef } from 'react';

/** One thing found on the open screen; `recordId` lets the palette skip the same record in the search of the whole system. */
export interface ScreenHit { id: string; label: string; hint?: string; icon?: string; recordId?: string; run: () => void }
/** What the screen finds for a query (already the screen's own rules: `from:` of the mail, the path of a page). */
export type ScreenSearch = (query: string) => ScreenHit[];

export const HERE_GROUP = 'На этом экране';
/** How many things of the open screen the palette shows before the rest of the system. */
export const HERE_LIMIT = 8;

// The screens mounted now, the last one on top: a screen inside another (a list beside its reader) speaks for the open screen.
const stack: { current: ScreenSearch }[] = [];

/** Puts a screen's search on top; the returned function takes it away (the hook calls both, tests call them directly). */
export function registerScreenSearch(entry: { current: ScreenSearch }): () => void {
  stack.push(entry);
  return () => { const i = stack.indexOf(entry); if (i >= 0) stack.splice(i, 1); };
}

/** The search of the open screen; while the screen is mounted the palette asks it first. The latest closure is always asked. */
export function useScreenSearch(search: ScreenSearch | null) {
  const ref = useRef(search);
  ref.current = search;
  const on = !!search;
  useEffect(() => (on ? registerScreenSearch({ current: (q) => ref.current?.(q) ?? [] }) : undefined), [on]);
}

/** What the open screen finds for the query (none without one): at most HERE_LIMIT; a failing screen does not break the palette. */
export function searchHere(query: string): ScreenHit[] {
  const top = stack[stack.length - 1];
  if (!top || !query.trim()) return [];
  try { return top.current(query).slice(0, HERE_LIMIT); } catch { return []; }
}

/**
 * The order of the palette: what is on the open screen first, then the whole system without the records already shown above.
 * Generic over the palette's options: `recordOf` names the record an option of the system leads to.
 */
export function hereFirst<T>(here: T[], rest: T[], hereRecords: Set<string>, recordOf: (o: T) => string | undefined): T[] {
  return [...here, ...rest.filter((o) => { const r = recordOf(o); return !r || !hereRecords.has(r); })];
}

// The palette is opened by the frame; a screen's own key («/» of the mail) asks the frame to open it.
const openers = new Set<() => void>();
export function onOpenSearch(cb: () => void) { openers.add(cb); return () => { openers.delete(cb); }; }
/** Opens the global search (the palette) — the place every screen searches in. */
export function openSearch() { for (const o of openers) o(); }
